/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Stack,
} from "@mui/material";
import FormField from "components/generals/FormInput";
import FormikForm from "components/layout/FormikForm";
import { Form, FormikState, Field } from "formik";
import { PersonInterface, UserInterface, UserRolesEnum } from "@malmur-technologies/medicalsystem-shared";
import { FC, useState } from "react";
import { checkFormValidity, userFormYUPValidation, userFormNoPassYUPValidation, autoTouchErrorFields } from "utils/form.utils";
import { FormProps } from "../../interfaces";
import useHttp from "hooks/http.hook";
import StepperFormActionButtons from "./custom/StepperFormActionButtons";
import Typography from "components/generals/Typography";
import ContactsIcon from "@mui/icons-material/Contacts";
import { FormActionType } from "types/form.enum";
import { CoreAPI_URLs } from "constants/http.const";
import Button from "components/generals/Button";
import Box from "components/generals/Box";
import { useTranslation } from "react-i18next";
import Checkbox from "components/generals/Checkbox";
import { authAction, useAppDispatch } from "store/store";
import { useNavigate } from "react-router-dom";
import { RoutesConstants } from "@malmur-technologies/medicalsystem-shared";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { EntityPermissions, PermissionsUser } from "types";
import FormInputPassword from "components/generals/FormInputPassword";
import { getCreatableRoles, isRootUser, canEditRoles } from "utils/rolePermissions.utils";
import { selectUserRoles, selectCurrentUser } from "store/slices/auth.slice";
import { useSelector } from "react-redux";
import { RootState } from "store/store";
import { selectGeneralSettings } from "store/slices/generalSettingsSlice";

/**
 * Props for UserForm component with stepper support.
 */
export interface UserFormProps extends FormProps<UserInterface> {
  person?: Partial<PersonInterface>;
  modifyPassword?: boolean;
  hideRoleSelector?: boolean;
}

/**
 * User account creation and management form with role assignment and hierarchical access control.
 * 
 * Features:
 * - Create new user accounts with username validation
 * - Edit existing user information
 * - Hierarchical role assignment with role-based visibility:
 *   - ROOT: Can create and see all roles (including ROOT)
 *   - ADMIN: Can create all roles except ROOT
 *   - MAINTAINER: Can create all roles except ROOT and ADMIN
 *   - DOCTOR, HEAD_NURSE, NURSE, SECRETARY_RECEPTIONIST: Can create themselves and PATIENT
 *   - PATIENT: Cannot create users
 * - Role selection visibility controlled by current user's permissions
 * - Password management (create/modify)
 * - Username uniqueness validation
 * - Integration with person data (linked to person entity)
 * - Stepper form support for multi-step workflows
 * - Auto-logout on password change for current user
 * - Form validation with Yup schema
 * - Permission-based save/delete actions (requires SELECT_ROLES permission)
 * - Error handling with modal dialogs
 * 
 * Role Hierarchy:
 * - Uses getCreatableRoles() utility to determine available roles
 * - Uses isRootUser() to control ROOT role visibility
 * - Implements principle of least privilege
 * 
 * Workflow:
 * 1. Load current user's roles from Redux store
 * 2. Calculate creatable roles based on user's permissions
 * 3. If stepper: Validate username uniqueness before proceeding
 * 4. If standalone: Submit directly to API
 * 5. On success: Update user list and close form
 * 6. If current user modified: Show logout warning
 * 
 * @param model - User data to edit (or new user template)
 * @param action - Form action (NEW, MODIFY)
 * @param closeAction - Callback when form is closed
 * @param nextStep - Callback for stepper next step
 * @param backStepHandler - Callback for stepper previous step
 * @param lastStep - Flag indicating last step in stepper
 * @param firstStep - Flag indicating first step in stepper
 * @param updateListHandler - Callback to refresh parent user list
 * @param person - Associated person entity data
 * @param modifyPassword - Flag to enable password modification
 * @param hideRoleSelector - Flag to explicitly hide role selector section
 * @returns User management form component with role hierarchy enforcement
 * 
 * Role Selector Visibility:
 * The role selector is automatically hidden in these scenarios:
 * 1. Stepper forms with predefined type (patient/doctor/nurse creation)
 * 2. User profile editing (users cannot change their own roles)
 * 3. When hideRoleSelector prop is explicitly set to true
 * 4. NewUserPage handles role selection via separate Autocomplete dropdown
 * 5. Editing existing users without ROOT or ADMIN privileges
 * 
 * Role Editing Permissions:
 * - Only ROOT and ADMIN users can edit roles of existing users
 * - This prevents unauthorized privilege escalation
 * - Doctors, nurses, and other roles can view but not modify user roles
 * - Applied to PatientFullForm, DoctorFullForm, and similar edit contexts
 */
const UserForm: FC<UserFormProps> = ({
  model,
  action,
  closeAction,
  nextStep,
  backStepHandler,
  lastStep,
  firstStep,
  updateListHandler,
  type,
  person,
  modifyPassword,
  hideRoleSelector
}) => {
  const [isLoading, _error, sendRequest] = useHttp();
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const readOnly = !nextStep ? true : false;
  const [messagePasswordFlag, setMessagePasswordFlag] = useState(false);
  const currentUserRoles = useSelector((state: RootState) => selectUserRoles(state));
  const currentUser = useSelector((state: RootState) => selectCurrentUser(state));
  const creatableRoles = getCreatableRoles(currentUserRoles);
  const isCurrentUserRoot = isRootUser(currentUserRoles);
  const canUserEditRoles = canEditRoles(currentUserRoles);
  const roles = Object.values(UserRolesEnum);
  modifyPassword = modifyPassword ?? false;
  const generalSettings = useSelector(selectGeneralSettings);
  const simplificatedClinic = generalSettings?.isSystemSimplified || false;
  // Check if user is editing their own profile
  const isEditingOwnProfile = action === FormActionType.MODIFY && currentUser?.id === model?.id;

  // Hide password fields in:
  // Creation (NEW): Backend assigns default password
  // Editing (MODIFY) with modifyPassword=true: Keeps current password
  const shouldHidePasswordFields = action === FormActionType.NEW || modifyPassword;

  const shouldHideRoleSelector = hideRoleSelector || (nextStep && type) || (action === FormActionType.MODIFY && !updateListHandler) || (action === FormActionType.MODIFY && updateListHandler && !canUserEditRoles);
  const submitHandler = async (submittedModel: Partial<UserInterface>) => {
    let modelToSend: Partial<UserInterface>;
    if (action === FormActionType.NEW || modifyPassword) {
      modelToSend = { ...submittedModel };
      // Preserve auth when modifying a user and keeping their existing password
      if (action === FormActionType.MODIFY && modifyPassword) {
        (modelToSend as UserInterface).auth = model.auth;
      }
    } else {
      modelToSend = submittedModel;
    }
    const pad = (n: number) => n.toString().padStart(2, "0");
    const now = new Date();
    const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;

    modelToSend.roles = modelToSend.roles?.map((role) => role ?? UserRolesEnum.SECRETARY_RECEPTIONIST);
    if (!modelToSend.username || modelToSend.username.trim().length === 0) {
      modelToSend.username = `NoUser${ts}`;
    }
    if (!modelToSend.email || modelToSend.email.trim().length === 0) {
      modelToSend.email = `no_email${ts}@example.com`;
    }
    const url = `${CoreAPI_URLs.USERS_GENERAL_OPERATIONS}?username=${modelToSend.username}`;
    const request = await sendRequest(url, null, "GET");
    if (nextStep) {
      if (request?.data?.length > 0) {
        handleOpenModal();
        return;
      } else {
        nextStep(modelToSend);
      }
    } else {
      if (action === FormActionType.MODIFY) {
        sendRequest(
          CoreAPI_URLs.USERS_GENERAL_OPERATIONS,
          onUserUpdateHandler,
          "PUT",
          JSON.stringify(modelToSend),
          null,
          { successMessage: t("userForm.userUpdated") }
        );
        const storedUser = JSON.parse(localStorage.getItem("user"));
        if (storedUser.id === modelToSend.id) {
          setMessagePasswordFlag(true);
          handleOpenModal();
        }
      }
    }
  };

  const onUserUpdateHandler = (updatedUser: UserInterface) => {
    updateListHandler(updatedUser);
  };

  const { t } = useTranslation();
  const handleOpenModal = () => {
    setOpenErrorModal(true);
  };
  const handleCloseErrorModal = () => {
    setOpenErrorModal(false);
    if (messagePasswordFlag) {
      logoutHandle();
    }
  };

  const appDispatch = useAppDispatch();
  const navigate = useNavigate();

  const logoutHandle = () => {
    appDispatch(authAction.logout());
    navigate(RoutesConstants.LOGIN);
  };

  return (
    <>
      <FormikForm
        onSubmit={(model) => {
          submitHandler(model);
        }}
        initialValues={{ ...model, roles: model.roles?.length ? model.roles : (type ? [type] : []) }}
        validationSchema={
          shouldHidePasswordFields
            ? userFormNoPassYUPValidation(t, simplificatedClinic)
            : (person
              ? userFormYUPValidation(t, model.username, person.lastname, person.name, simplificatedClinic)
              : userFormYUPValidation(t, undefined, undefined, undefined, simplificatedClinic)
            )
        }
        enableReinitialize={true}
      >
        {(formikProps: FormikState<Partial<UserInterface>> & { setFieldValue: (field: string, value: any) => void; setFieldTouched: (field: string, touched: boolean, shouldValidate?: boolean) => void }) => {
          const { values, errors, touched, setFieldTouched } = formikProps;
          // Auto-touch effect for required fields with errors (DoctorForm pattern)
          const hasMarkedFieldsRef = useState<{ current: boolean }>({ current: false })[0];
          if (checkFormValidity(values, errors, touched) && !hasMarkedFieldsRef.current) {
            hasMarkedFieldsRef.current = true;
            setTimeout(() => {
              // @ts-ignore: autoTouchErrorFields is generic
              autoTouchErrorFields(values, errors, touched, setFieldTouched);
            }, 0);
          } else if (!checkFormValidity(values, errors, touched)) {
            hasMarkedFieldsRef.current = false;
          }
          return (
            <>
              {values && (
                <Form id="userForm" autoComplete="off">
                  <Stack alignItems="center" direction="row" gap={2} mb={3}>
                    <ContactsIcon fontSize="large" />
                    <Typography variant="h4">{t("userForm.onlineUserAccountData")}</Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6} lg={6}>
                      <FormField
                        type="text"
                        label={t("userForm.userName")}
                        name="username"
                        readOnly={readOnly}
                        value={values.username || ""}
                        error={errors.username && touched.username}
                      />
                    </Grid>
                    <Grid item xs={12} md={6} lg={6}>
                      <FormField
                        type="email"
                        label={t("userForm.email")}
                        name="email"
                        value={values.email || ""}
                        error={errors.email && touched.email}
                      />
                    </Grid>
                    {!shouldHidePasswordFields && (
                      <>
                        <Grid item xs={12} md={6} lg={6}>
                          <FormInputPassword
                            label={t("userForm.password")}
                            name="auth.password"
                            value={undefined}
                            fullWidth
                            forceHidePassword={true}
                            autoComplete="new-password"
                            error={errors.auth?.password && touched.auth?.password}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} lg={6}>
                          <FormInputPassword
                            label={t("userForm.repeatPassword")}
                            name="repeatPassword"
                            value={undefined}
                            fullWidth
                            forceHidePassword={true}
                            error={errors.auth?.password && touched.auth?.password}
                          />
                        </Grid>
                      </>
                    )}
                    {!shouldHideRoleSelector && (
                      <PermissionWrapper
                        requiredPermissions={[
                          PermissionsUser[EntityPermissions.NEW_PATIENTS].SELECT_ROLES,
                          PermissionsUser[EntityPermissions.NEW_DOCTORS].SELECT_ROLES,
                        ]}
                      >
                        <Grid item xs={12} md={12} lg={12} sm={12} >
                          <Stack id="checkbox-group">
                            <Typography variant="h5" mb={2}>{t("userForm.selectRoles")}</Typography>
                          </Stack>
                          <div role="group" aria-labelledby="checkbox-group">
                            <Grid container gap={{ xs: 2, sm: 1, md: 3, lg: 1 }} mb={1} direction="row" >
                              {roles.map((role) => {
                                if (role === UserRolesEnum.ROOT && !isCurrentUserRoot) {
                                  return null;
                                }

                                const isRoleAlreadyAssigned = values.roles?.includes(role);
                                const canCreateRole = creatableRoles.includes(role);

                                if (!canCreateRole && !isRoleAlreadyAssigned) {
                                  return null;
                                }

                                const isTypeRole = role === type;
                                const isChecked = values.roles?.includes(role) || false;
                                const isDisabled = isTypeRole || (isRoleAlreadyAssigned && !canCreateRole);

                                return (
                                  <label key={role}>
                                    <Stack alignItems="center" direction="row" gap={1}>
                                      <Field
                                        type="checkbox"
                                        name="roles"
                                        value={role}
                                        checked={isChecked}
                                        as={Checkbox}
                                        disabled={isDisabled}
                                        label={t(`userForm.${role.toLowerCase().replace("/", "").replace(" ", "")}`)}
                                      />
                                    </Stack>
                                  </label>
                                );
                              })}
                            </Grid>

                          </div>
                        </Grid>
                      </PermissionWrapper>
                    )}
                  </Grid>
                  {checkFormValidity(values, errors, touched) && (
                    <Box mt={2} mb={2}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'red',
                          textAlign: 'center'
                        }}
                      >
                        {checkFormValidity(values, errors, touched)}
                      </Typography>
                    </Box>
                  )}
                  {action === FormActionType.MODIFY ? (
                    isEditingOwnProfile ? (
                      <Box mt={2} width="100%" display="flex" justifyContent="flex-end">
                        <Button
                          name="cy-save-user"
                          type="submit"
                          variant="contained"
                          color="info"
                          disabled={isLoading}
                        >
                          {t("formActionButtons.save")}
                        </Button>
                      </Box>
                    ) : (
                      <PermissionWrapper
                        requiredPermissions={[
                          PermissionsUser[EntityPermissions.NEW_PATIENTS].EDIT,
                          PermissionsUser[EntityPermissions.NEW_DOCTORS].EDIT,
                          PermissionsUser[EntityPermissions.NURSE].EDIT,
                          PermissionsUser[EntityPermissions.USER_PROFILE].EDIT,
                        ]}
                      >
                        <Box mt={2} width="100%" display="flex" justifyContent="flex-end">
                          <Button
                            name="cy-save-user"
                            type="submit"
                            variant="contained"
                            color="info"
                            disabled={isLoading}
                          >
                            {t("formActionButtons.save")}
                          </Button>
                        </Box>
                      </PermissionWrapper>
                    )
                  ) : (
                    <StepperFormActionButtons
                      backStepHandler={backStepHandler}
                      nextStep={nextStep}
                      firstStep={firstStep}
                      lastStep={lastStep}
                      isLoading={isLoading}
                    />
                  )}
                </Form>
              )}
            </>
          )
        }
        }
      </FormikForm>
      <Dialog
        open={openErrorModal}
        onClose={handleCloseErrorModal}
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
      >
        <DialogTitle id="error-dialog-title">
          {messagePasswordFlag ? t("userForm.alert") : t("userForm.sorry")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="error-dialog-description">
            {messagePasswordFlag
              ? t("userForm.redirectionLoggin")
              : t("userForm.usernameAlreadyExist")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button id="cy-close-user-modal" onClick={handleCloseErrorModal} color="primary">
            {t("userForm.ok")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserForm;
